// Hook reutilizável para gerenciar histórico de conversas de um personagem em um modo específico
// Carrega ao montar, salva ao adicionar mensagens, e pruneia automaticamente para usuários gratuitos
import { useState, useEffect, useCallback, useContext } from 'react';
import { CoreContext } from 'context/CoreContext';
import {
    getStoredMessages,
    saveMessage as saveMessageToStorage,
    deleteConversation,
    pruneOldMessages,
    RETENTION_DAYS_FREE
} from 'services/conversationHistory';
import useSubscriptionCheck from 'hooks/useSubscriptionCheck';

/**
 * Hook para gerenciar o histórico de conversas de UM personagem em UM modo
 *
 * @param {string} characterId - 'salomao' | 'daniel' | 'ester' | 'davi'
 * @param {string} mode - 'chat' (Bate-papo) | 'learn' (Praticar Idiomas)
 * @returns {{
 *   loadedMessages: Array,
 *   saveMessage: Function,
 *   clearHistory: Function,
 *   isReady: boolean
 * }}
 */
const useConversationHistory = (characterId, mode) => {
    const { user } = useContext(CoreContext);
    const { hasActiveSubscription } = useSubscriptionCheck();
    const [loadedMessages, setLoadedMessages] = useState([]);
    const [isReady, setIsReady] = useState(false);

    useEffect(() => {
        if (!user?.id || !characterId || !mode) {
            setIsReady(true);
            return;
        }

        // Pruning automático para usuários gratuitos (Premium tem histórico ilimitado)
        if (hasActiveSubscription === false) {
            pruneOldMessages(user.id, RETENTION_DAYS_FREE);
        }

        const messages = getStoredMessages(user.id, characterId, mode);
        setLoadedMessages(messages);
        setIsReady(true);
    }, [user?.id, characterId, mode, hasActiveSubscription]);

    const saveMessage = useCallback((message) => {
        if (!user?.id || !characterId || !mode || !message) return;
        saveMessageToStorage(user.id, characterId, mode, message);
    }, [user?.id, characterId, mode]);

    const clearHistory = useCallback(() => {
        if (!user?.id || !characterId || !mode) return;
        deleteConversation(user.id, characterId, mode);
        setLoadedMessages([]);
    }, [user?.id, characterId, mode]);

    return {
        loadedMessages,
        saveMessage,
        clearHistory,
        isReady
    };
};

export default useConversationHistory;
